import React, { useState, useRef, useEffect } from 'react';
import { useAppSelector } from '../hooks/useRedux';

interface PensionMap {
  [freelancerId: string]: { pension: boolean; pensionAmount: number };
}
interface HolidayMap {
  [freelancerId: string]: { holidaySavings: boolean; holidaySavingsFee: number };
}

interface ModalPensionHolidaySectionProps {
  freelancerId: string;
  freelancerName?: string;
  isRegularEngagement: boolean;
  globalData?: any;
  // Per-freelancer calculated gross (SEK) used for the 5000 SEK pension rule
  calculatedAmount?: number;
  t: (key: string) => string;
  pensionData: PensionMap;
  holidayData: HolidayMap;
  onPensionChange: (data: PensionMap) => void;
  onHolidayChange: (data: HolidayMap) => void;
}

const PENSION_OPTIONS = [1000, 1500, 2000, 2500, 3000];
const HOLIDAY_OPTIONS = [12, 20, 30, 40, 50];

/**
 * Compact pension + holiday controls for the global "Add Multiple Rows" modal.
 * Mirrors the freelancer-card behaviour (enable checkbox + amount dropdown +
 * the 5000 SEK / regular-engagement / isPensionAllowed validation) and writes
 * to the SAME pensionDataByFreelancer / holidayDataByFreelancer state so values
 * pre-fill and stay in sync with the cards. Only rendered for a specific
 * freelancer (never for "All").
 */
const ModalPensionHolidaySection: React.FC<ModalPensionHolidaySectionProps> = ({
  freelancerId,
  freelancerName,
  isRegularEngagement,
  globalData,
  calculatedAmount,
  t,
  pensionData,
  holidayData,
  onPensionChange,
  onHolidayChange,
}) => {
  const { freelancers } = useAppSelector((state) => state.customer);
  const freelancerObj = freelancers?.find((f: any) => f?.userId?.toString() === freelancerId);
  const isPensionAllowed = freelancerObj?.isPensionAllowed ?? true;

  // Data used to reconstruct WHY pension is unavailable (backend only sends the
  // isPensionAllowed boolean, so we explain it from the freelancer's own fields).
  // The freelancer's profile fields (country, ssn) live under `signUpUser`.
  const freelancerProfile = freelancerObj?.signUpUser || freelancerObj || {};
  const freelancerCountry = (freelancerProfile?.country ?? freelancerObj?.country ?? '').toString().trim();
  const hasCountry = freelancerCountry.length > 0;
  const isSweden =
    freelancerCountry.toLowerCase() === 'sweden' ||
    freelancerCountry.toUpperCase() === 'SE' ||
    freelancerCountry.toLowerCase() === 'sverige';
  // FIRST gate: the freelancer must be based in Sweden (checked before 5000 SEK / age).
  const isNotSweden = hasCountry && !isSweden;
  // Swedish SSN is 12 digits; last 4 = "0000" means it's not a valid personnummer.
  const freelancerSsnDigits = (freelancerProfile?.ssn ?? freelancerObj?.ssn ?? '').toString().replace(/\D/g, '');
  const ssnInvalid = freelancerSsnDigits.length === 12 && freelancerSsnDigits.slice(-4) === '0000';

  const defaultHolidayFee = globalData?.holidaySavingsFee?.[0]?.globalValue || 12;

  const currentPension = pensionData[freelancerId] || { pension: false, pensionAmount: PENSION_OPTIONS[0] };
  const currentHoliday = holidayData[freelancerId] || { holidaySavings: false, holidaySavingsFee: defaultHolidayFee };

  const isAmountValid = Number(calculatedAmount || 0) >= 5000.0;

  const [pensionOpen, setPensionOpen] = useState(false);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const pensionDropRef = useRef<HTMLDivElement>(null);
  const holidayDropRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pensionDropRef.current && !pensionDropRef.current.contains(e.target as Node)) setPensionOpen(false);
      if (holidayDropRef.current && !holidayDropRef.current.contains(e.target as Node)) setHolidayOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePensionToggle = (checked: boolean) => {
    // Only allow enabling pension when it's actually eligible; disabled UI guards
    // this too, but keep the state guard as a safety net.
    if (checked && !pensionEligible) return;
    onPensionChange({
      ...pensionData,
      [freelancerId]: {
        ...currentPension,
        pension: checked,
        pensionAmount: checked ? currentPension.pensionAmount || PENSION_OPTIONS[0] : 0,
      },
    });
  };

  const handlePensionAmount = (amount: number) => {
    onPensionChange({
      ...pensionData,
      [freelancerId]: { ...currentPension, pension: true, pensionAmount: amount },
    });
    setPensionOpen(false);
  };

  const handleHolidayToggle = (checked: boolean) => {
    onHolidayChange({
      ...holidayData,
      [freelancerId]: {
        ...currentHoliday,
        holidaySavings: checked,
        holidaySavingsFee: currentHoliday.holidaySavingsFee || defaultHolidayFee,
      },
    });
  };

  const handleHolidayFee = (fee: number) => {
    onHolidayChange({
      ...holidayData,
      [freelancerId]: { ...currentHoliday, holidaySavingsFee: fee },
    });
    setHolidayOpen(false);
  };

  // Pension stays VISIBLE even when it can't be taken — we disable it and explain
  // why, rather than hiding it. Eligibility = regular engagement + pension allowed
  // for this freelancer + gross >= 5000 SEK.
  const pensionEligible = isPensionAllowed && !isNotSweden && isRegularEngagement && isAmountValid;
  // Reason is resolved in priority order — the first failing check wins:
  //  1. Country is not Sweden (checked first)
  //  2. Gross < 5000 SEK
  //  3. SSN is not a valid Swedish personnummer (last 4 digits are 0000)
  //  4. Not a Regular/Continuous engagement
  //  5. Fallback (Sweden + valid SSN, but pension still not allowed) → freelancer is above 66
  const pensionDisabledReason = isNotSweden
    ? (t('PENSION_DISABLED_COUNTRY') || 'Pension is only available for freelancers based in Sweden.')
    : !isAmountValid
    ? (t('PENSION_ERROR_GROSS_SALARY') || 'The pension option is only available if the gross salary is 5000 SEK or more.')
    : ssnInvalid
    ? (t('PENSION_DISABLED_SSN') || 'Pension requires a valid Swedish personal number (SSN) for this freelancer.')
    : !isRegularEngagement
    ? (t('AVAILABLE_ONLY_REGULAR_ENGAGEMENT') || 'Available only for Regular/Continuous engagement type.')
    : (t('PENSION_DISABLED_AGE') || 'Pension is not available because this freelancer is above 66 years of age.');

  const pensionChecked = pensionEligible && currentPension.pension;
  const holidayChecked = isRegularEngagement && currentHoliday.holidaySavings;

  // Pension is always shown (disabled + reason when ineligible).
  // Holiday is only relevant in a regular engagement.
  const showHoliday = isRegularEngagement;

  const renderDropdown = (
    open: boolean,
    setOpen: (v: boolean) => void,
    value: number,
    options: number[],
    onSelect: (v: number) => void,
    ref: React.RefObject<HTMLDivElement>,
    suffix?: string
  ) => (
    <div className="ag-ph-dropdown" ref={ref}>
      <button type="button" className="ag-ph-dropdown-toggle" onClick={() => setOpen(!open)}>
        <span>
          {value}
          {suffix ? ` ${suffix}` : ''}
        </span>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="ag-ph-dropdown-menu">
          {options.map((opt) => (
            <button
              type="button"
              key={opt}
              className={`ag-ph-dropdown-item${opt === value ? ' selected' : ''}`}
              onClick={() => onSelect(opt)}
            >
              {opt}
              {suffix ? ` ${suffix}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="ag-ph-section">
      <div className="ag-ph-title">{t('PENSION_HOLIDAY') || 'Pension & Holiday Pay'}</div>

      {/* Pension — always visible; disabled with a reason when it can't be taken */}
      <div className={`ag-ph-card${!pensionEligible ? ' is-disabled' : ''}`}>
        <label className={`ag-ph-row${!pensionEligible ? ' is-disabled' : ''}`}>
          <input
            type="checkbox"
            className="ag-ph-check"
            disabled={!pensionEligible}
            checked={pensionChecked}
            onChange={(e) => handlePensionToggle(e.target.checked)}
          />
          <span className="ag-ph-label">
            {t('PENSION') || 'Pension'}
            {freelancerName ? <em className="ag-ph-name"> ({freelancerName})</em> : null}
          </span>
          {pensionChecked && (
            renderDropdown(pensionOpen, setPensionOpen, currentPension.pensionAmount || PENSION_OPTIONS[0], PENSION_OPTIONS, handlePensionAmount, pensionDropRef)
          )}
        </label>
        {pensionEligible ? (
          <div className="ag-ph-note">{t('PENSION_NOTE_5000_SEK')}</div>
        ) : (
          <div className="ag-ph-note ag-ph-reason">{pensionDisabledReason}</div>
        )}
      </div>

      {/* Holiday Pay — only visible in a regular engagement */}
      {showHoliday && (
      <div className="ag-ph-card">
        <label className="ag-ph-row">
          <input
            type="checkbox"
            className="ag-ph-check"
            disabled={!isRegularEngagement}
            checked={holidayChecked}
            onChange={(e) => handleHolidayToggle(e.target.checked)}
          />
          <span className="ag-ph-label">
            {t('HOLIDAY_PAY') || 'Holiday Pay'}
            {freelancerName ? <em className="ag-ph-name"> ({freelancerName})</em> : null}
          </span>
          {holidayChecked && (
            renderDropdown(holidayOpen, setHolidayOpen, currentHoliday.holidaySavingsFee || defaultHolidayFee, HOLIDAY_OPTIONS, handleHolidayFee, holidayDropRef, '%')
          )}
        </label>
        <div className="ag-ph-note">{t('HOLIDAY_PAY_NOTE_190_SEK')}</div>
      </div>
      )}
    </div>
  );
};

export default ModalPensionHolidaySection;
